import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import useGetTranslatedAttribute from 'hooks/useGetTranslatedAttribute';
import ItemCategory from 'types/ItemCategory';

import CustomLoader from '../../components/CustomLoader/CustomLoader';
import CustomTablePagination from '../../components/CustomTablePagination/CustomTablePagination';
import CategoryDetailsDialog from '../../dialogs/CategoryDetailsDialog/CategoryDetailsDialog';
import api from '../../utils/api';
import { ActionTableCell, FlexContainer, StyledButton, Title } from './styled';

const ItemCategoriesPage = () => {
    const { t } = useTranslation();
    const getTranslatedAttribute = useGetTranslatedAttribute();

    const [isLoading, setIsLoading] = useState(true);
    const [categories, setCategories] = useState<ItemCategory[]>([]);
    const [selectedCategoryId, selectCategoryId] = useState(0);

    const [paginationPage, setPaginationPage] = useState(0);
    const [paginationTotal, setPaginationTotal] = useState(0);
    const [paginationItemsPerPage, setPaginationItemsPerPage] = useState(25);

    const [isCategoryDialogOpen, setCategoryDialogOpen] = useState(false);

    const getData = useCallback(() => {
        api.request('/item_categories', 'GET', { page: paginationPage + 1, perPage: paginationItemsPerPage }).then((res) => {
            const { data, total } = res;
            setCategories(data);
            setPaginationTotal(total);
            setIsLoading(false);
        });
    }, [paginationPage, paginationItemsPerPage]);

    const openCategoryDialog = useCallback((categoryId: number) => {
        selectCategoryId(categoryId);
        setCategoryDialogOpen(true);
    }, [selectCategoryId, setCategoryDialogOpen]);

    const closeCategoryDialog = useCallback((categoryId?: number) => {
        if (categoryId) {
            selectCategoryId(categoryId);
        }
        setCategoryDialogOpen(false);
    }, [setCategoryDialogOpen]);

    useEffect(() => {
        if (!isCategoryDialogOpen) getData();
    }, [getData, isCategoryDialogOpen]);

    useEffect(() => {
        setPaginationPage(0);
    }, [paginationItemsPerPage]);

    if (isLoading) {
        return <CustomLoader />;
    }

    return (
        <>
            <FlexContainer>
                <Title variant='h4'>{t('pages.item_categories.title')}</Title>
            </FlexContainer>
            <TableContainer component={Paper} style={{ marginTop: '20px' }} className='book-list-table-view'>
                <Table aria-label='simple table'>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('pages.item_categories.table.name')}</TableCell>
                            <TableCell>{t('pages.generic.table.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {categories.map(category => {

                            return (
                                <TableRow key={category.id}>
                                    <TableCell style={category.parent ? { paddingLeft: '36px' } : undefined}>{getTranslatedAttribute(category, 'name')}</TableCell>
                                    <ActionTableCell>
                                        <StyledButton variant='outlined' color='primary' onClick={() => openCategoryDialog(category.id)}>
                                            {t('pages.languages.table.translation')}
                                        </StyledButton>
                                    </ActionTableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <CustomTablePagination
                count={paginationTotal}
                page={paginationPage}
                onPageChange={(_e, page) => setPaginationPage(page)}
                rowsPerPage={paginationItemsPerPage}
                onRowsPerPageChange={(e) => setPaginationItemsPerPage(parseInt(e.target.value, 10))}
            />

            <CategoryDetailsDialog open={isCategoryDialogOpen} onClose={closeCategoryDialog} categoryId={selectedCategoryId} />
        </>
    );
};

export default ItemCategoriesPage;
